import React, { useContext } from 'react';

import { Toaster } from 'react-hot-toast';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { InformativosProvider } from './context/InformativosContext';
import Login from './pages/login/Login';
import Dashboard from './pages/dashboard/Dashboard';
import MainLayout from './components/layout/MainLayout';
import Courses from './pages/courses/Courses';
import Modules from './pages/modules/Modules';
import ModuleDetail from './pages/moduleDetail/ModuleDetail';
import UsersList from './pages/user/UsersList';
import UserProfile from './pages/userProfile/UserProfile';
import SuperAdminDashboard from './pages/superAdmin/SuperAdminDashboard';
import SuperAdminCompanies from './pages/superAdmin/components/SuperAdminCompanies';
import SuperAdminUsers from './pages/superAdmin/components/SuperAdminUsers';
import SuperAdminExclusions from './pages/superAdmin/components/SuperAdminExclusions';
import ReportsDashboard from './pages/reportsDashboard/ReportsDashboard';
import Informativos from './pages/informativos/Informativos';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

function App() {
  const darkTheme = createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#3b82f6', // --accent-blue
      },
      secondary: {
        main: '#ec4899', // --accent-pink
      },
      background: {
        default: '#1e293b', // --bg-secondary
        paper: '#334155', // --bg-tertiary
      },
      text: {
        primary: '#ffffff', // --text-primary
        secondary: '#cbd5e1', // --text-secondary
      },
    },
  });

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <AuthProvider>
        <InformativosProvider>
          {/* O Toaster é o componente que renderiza as notificações.
          */}
          <Toaster
            position="top-right"
            toastOptions={{
              // Duração padrão para todos os toasts
              duration: 4000,

              // Estilo base para todos os toasts (tema claro)
              style: {
                // Adapta as notificações para o tema escuro
                background: '#0f172a',
                color: '#e6eef8',
                border: '1px solid rgba(59,130,246,0.08)',
                borderRadius: '8px',
                boxShadow: '0 8px 16px rgba(0,0,0,0.6)',
                fontSize: '16px',
                padding: '12px 16px',
              },

              // Estilo específico para toasts de sucesso
              success: {
                duration: 2000, // Sucesso pode ser mais rápido
              },
            }}
          />
          <Router>
            <div className="App">
              <Routes>
                <Route path="/login" element={<Login />} />
                {/* A rota de registro pode ou não ser pública, ajuste conforme necessário */}
                {/* <Route path="/register" element={<Register />} /> */}

                {/* Rotas Protegidas */}
                <Route element={<MainLayout />}>
                  {/* Rotas do Painel de Controle (Super Admin) */}
                  <Route
                    path="/control-panel"
                    element={
                      <PrivateRoute allowedRoles={['SUPER_ADMIN', 'ADMIN_CLIENT', 'ADMIN_EAD']}>
                        <SuperAdminDashboard />
                      </PrivateRoute>
                    }
                  >
                    <Route index element={<Navigate to="companies" replace />} />
                    <Route path="companies" element={<SuperAdminCompanies />} />
                    <Route path="users" element={<SuperAdminUsers />} />
                    <Route path="exclusions" element={<SuperAdminExclusions />} />
                  </Route>

                  {/* Rotas para outras roles (instructor, student) */}
                  <Route
                    path="/"
                    element={<PrivateRoute><Dashboard /></PrivateRoute>}
                  />
                  <Route
                    path="/users"
                    element={<PrivateRoute allowedRoles={['ADMIN_EAD', 'ADMIN_CLIENT', 'INSTRUCTOR']}><UsersList /></PrivateRoute>}
                  />
                  <Route
                    path="/user/:userName"
                    element={<PrivateRoute><UserProfile /></PrivateRoute>}
                  />
                  <Route
                    path="/profile"
                    element={<PrivateRoute isProfileRoute={true}><UserProfile /></PrivateRoute>}
                  />
                  <Route
                    path="/courses"
                    element={<PrivateRoute allowedRoles={['ADMIN_EAD', 'INSTRUCTOR']}><Courses /></PrivateRoute>}
                  />
                  <Route
                    path="/courses/:courseId/modules"
                    element={<PrivateRoute allowedRoles={['ADMIN_EAD', 'INSTRUCTOR']}><Modules /></PrivateRoute>}
                  />
                  <Route
                    path="/courses/:courseId/modules/:moduleId"
                    element={<PrivateRoute allowedRoles={['ADMIN_EAD', 'INSTRUCTOR']}><ModuleDetail /></PrivateRoute>}
                  />
                  <Route
                    path="/reports"
                    element={<PrivateRoute allowedRoles={['ADMIN_EAD', 'ADMIN_CLIENT', 'INSTRUCTOR']}><ReportsDashboard /></PrivateRoute>}
                  />
                  <Route
                    path="/informativos"
                    element={<PrivateRoute><Informativos /></PrivateRoute>}
                  />
                </Route>

                {/* Rota de fallback */}
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </div>
          </Router>
        </InformativosProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

function PrivateRoute({ children, allowedRoles, isProfileRoute = false }) {
  const { user, loading } = useContext(AuthContext);
  const token = localStorage.getItem('token');

  if (loading) return <div>Carregando...</div>;

  if (!token || !user) return <Navigate to="/login" />;

  // Se o usuário é super_admin, ele só pode acessar rotas de super_admin
  if (user.role === 'SUPER_ADMIN' && !isProfileRoute && !allowedRoles?.includes('SUPER_ADMIN')) return <Navigate to="/control-panel" />;

  // Se a rota é exclusiva para super_admin e o usuário não é, redireciona para o dashboard principal
  if (allowedRoles?.includes('SUPER_ADMIN') && user.role !== 'SUPER_ADMIN') {
    return <Navigate to="/" />;
  }

  // Se a rota tem perfis definidos e o usuário não pertence a eles (e não é super_admin), redireciona
  if (allowedRoles && !allowedRoles.includes(user.role) && user.role !== 'SUPER_ADMIN') {
    return <Navigate to="/" />;
  }

  return children;
}

export default App;